import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Play,
  PauseCircle,
  FlaskConical,
  Power,
  Clock,
  Trash2,
} from 'lucide-react';
import {
  ApiError,
  deleteSchedule,
  listSchedules,
  runAppBatch,
  runBatch,
  updateAppBatchSettings,
  updateCoreBatchSettings,
} from '../api/client.js';
import CronEditor from '../components/CronEditor.jsx';

/**
 * Planification : ce qui est prévu, quand, et dans quel mode.
 *
 * Générique : la vue rend ce que le registre du back déclare. Un batch ajouté
 * côté serveur apparaît ici sans modification.
 *
 * Deux informations comptent autant que l'horaire :
 *   • le MODE — un batch planifié en simulation n'écrit rien, et l'ignorer
 *     ferait croire à tort que le référentiel est tenu à jour ;
 *   • l'ÉTAT — sans configuration (identifiants absents), la planification
 *     existe mais ne peut rien faire.
 *
 * Le mode et l'armement sont MODIFIABLES ici pour les batchs qui le déclarent :
 * basculer une synchronisation de la simulation vers l'écriture est une
 * décision d'exploitation, elle n'a pas à passer par un déploiement. La console
 * s'adresse alors à l'application qui exécute le batch (cf. `appBaseUrl`), et
 * non à synocore, qui n'a aucun chemin d'exécution vers elle.
 */

function formatDate(iso, timezone) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-CH', {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return new Date(iso).toLocaleString('fr-CH');
  }
}

/** Délai restant, en clair — plus parlant qu'une date pour « c'est quand ? ». */
function untilLabel(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diff) || diff < 0) return null;
  const min = Math.round(diff / 60_000);
  if (min < 60) return `dans ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `dans ${h} h ${String(min % 60).padStart(2, '0')}`;
  return `dans ${Math.floor(h / 24)} j`;
}

/**
 * Un batch est pilotable quand l'application qui l'exécute est joignable
 * depuis le navigateur et qu'elle déclare accepter la relance. Les batchs de
 * synocore, eux, passent par la route de relance du core (`onRun`).
 */
function isAppControlled(schedule) {
  return schedule.app !== 'synocore' && schedule.runnable === true;
}

/**
 * Un batch est réglable dès lors que quelqu'un sait recevoir l'ordre : synocore
 * pour ses propres batchs, l'application elle-même pour les autres.
 */
function isControllable(schedule) {
  return schedule.app === 'synocore' || isAppControlled(schedule);
}

/**
 * Seules les planifications ANNONCÉES se suppriment : un batch de synocore est
 * déclaré dans le code et serait réécrit au prochain démarrage. On le met en
 * pause — ce que l'API rappelle si on insiste.
 */
function isRemovable(schedule) {
  return schedule.app !== 'synocore';
}

function ScheduleCard({ schedule, onRun, onSettings, onDelete, running }) {
  const next = schedule.nextRuns?.[0] ?? null;
  const until = untilLabel(next);
  const [confirm, setConfirm] = useState(null); // null | 'dry' | 'apply'
  // Confirmation distincte pour le passage en écriture : c'est le réglage qui
  // engage, pas celui qui remet en simulation.
  const [confirmApply, setConfirmApply] = useState(false);
  const [editingCron, setEditingCron] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const controlled = isControllable(schedule);

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 'var(--radius-md)',
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--shadow-card)',
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15 }}>{schedule.label}</strong>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {schedule.type} · {schedule.app}
            </span>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
            {schedule.description}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Trois états, pas deux : « non configuré » (l'environnement ne
              permet pas d'exécuter) et « en pause » (décision d'exploitation)
              n'appellent pas la même action. */}
          {schedule.configured === false ? (
            <Badge color="var(--text-muted)" Icon={PauseCircle} label="Non configuré" />
          ) : schedule.enabled ? (
            <Badge color="var(--success)" Icon={CheckCircle2} label="Planifié" />
          ) : (
            <Badge color="var(--warning)" Icon={PauseCircle} label="En pause" />
          )}
          {schedule.applies ? (
            <Badge color="var(--warning)" Icon={Play} label="Écrit en base" />
          ) : (
            <Badge color="var(--primary)" Icon={FlaskConical} label="Simulation" />
          )}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginTop: 14,
        }}
      >
        <Field label="Fréquence">
          {schedule.cronLabel ?? schedule.cron ?? 'Non planifié'}
          {schedule.cron && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>
              {schedule.cron} · {schedule.timezone}
            </div>
          )}
          {schedule.overridden && (
            <div style={{ fontSize: 10.5, color: 'var(--warning)', marginTop: 2 }}>
              Réglé depuis la console
              {schedule.updatedBy ? ` par ${schedule.updatedBy}` : ''}
            </div>
          )}
        </Field>

        <Field label="Prochaine exécution">
          {schedule.enabled && next ? (
            <>
              {formatDate(next, schedule.timezone)}
              {until && (
                <div style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 600 }}>{until}</div>
              )}
            </>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>Aucune</span>
          )}
        </Field>

        <Field label="Dernière exécution">
          {schedule.lastRun ? (
            <>
              {formatDate(schedule.lastRun.startedAt, schedule.timezone)}
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {schedule.lastRun.status}
                {schedule.lastRun.dryRun ? ' (simulation)' : ''}
                {schedule.lastRun.triggeredBy ? ` · ${schedule.lastRun.triggeredBy}` : ''}
              </div>
            </>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>Jamais</span>
          )}
        </Field>
      </div>

      {/*
        Écart entre ce qui est DEMANDÉ et ce que le moteur a POSÉ.
        C'était le principal risque de rendre la planification modifiable : un
        réglage accepté mais non appliqué resterait invisible jusqu'à la
        prochaine exécution manquée. On le montre plutôt que de l'espérer.
      */}
      {schedule.app === 'synocore' &&
        schedule.schedulerActive === false &&
        schedule.enabled &&
        schedule.configured !== false && (
          <p
            style={{
              margin: '10px 0 0',
              padding: 8,
              fontSize: 12,
              color: 'var(--danger)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Cette planification est
            armée mais <strong>aucun cron n'est actif</strong> dans le moteur. Le batch ne se
            déclenchera pas tout seul. Cause la plus fréquente : le moteur de jobs n'a pas
            démarré — vérifier les journaux de l'application.
          </p>
        )}

      {schedule.nextRuns?.length > 1 && (
        <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--text-muted)' }}>
          Puis :{' '}
          {schedule.nextRuns
            .slice(1)
            .map((d) => formatDate(d, schedule.timezone))
            .join(' · ')}
        </p>
      )}

      {controlled && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--glass-border)', paddingTop: 12 }}>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', letterSpacing: 0.4, marginBottom: 8 }}>
            RÉGLAGES
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {/* Mode : le passage en écriture demande une confirmation, le
                retour en simulation non — on ne freine pas la marche arrière. */}
            {schedule.applies && !confirmApply ? (
              <ActionButton
                onClick={() => onSettings(schedule, { applies: false })}
                disabled={running}
                Icon={FlaskConical}
                label="Repasser en simulation"
                tone="neutral"
              />
            ) : confirmApply ? (
              <div style={{ flex: '1 1 100%' }}>
                <p style={{ margin: '0 0 8px', fontSize: 12.5 }}>
                  <AlertTriangle size={14} style={{ verticalAlign: -2, color: 'var(--warning)' }} />{' '}
                  À partir de la prochaine exécution, ce batch <strong>écrira en base</strong>.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <ActionButton
                    onClick={() => {
                      onSettings(schedule, { applies: true });
                      setConfirmApply(false);
                    }}
                    disabled={running}
                    Icon={Play}
                    label="Confirmer le passage en écriture"
                    tone="danger"
                  />
                  <ActionButton onClick={() => setConfirmApply(false)} label="Annuler" tone="ghost" />
                </div>
              </div>
            ) : (
              <ActionButton
                onClick={() => setConfirmApply(true)}
                disabled={running}
                Icon={Play}
                label="Passer en écriture"
                tone="neutral"
              />
            )}

            {/* Armement : mettre en pause n'annule rien, la relance manuelle
                reste possible — c'est précisément l'usage de la pause. */}
            <ActionButton
              onClick={() => onSettings(schedule, { enabled: !schedule.enabled })}
              disabled={running || (schedule.configured === false && !schedule.enabled)}
              Icon={Power}
              label={schedule.enabled ? 'Mettre la planification en pause' : 'Réarmer la planification'}
              tone="neutral"
            />

            {/* La fréquence n'est modifiable que là où synocore exécute : une
                application tierce reste maîtresse de son propre cron. */}
            {schedule.app === 'synocore' && !editingCron && (
              <ActionButton
                onClick={() => setEditingCron(true)}
                disabled={running}
                Icon={Clock}
                label="Modifier la fréquence"
                tone="neutral"
              />
            )}

            {isRemovable(schedule) && !confirmDelete && (
              <ActionButton
                onClick={() => setConfirmDelete(true)}
                disabled={running}
                Icon={Trash2}
                label="Retirer de l'annuaire"
                tone="ghost"
              />
            )}
          </div>

          {editingCron && (
            <div style={{ marginTop: 4, marginBottom: 10 }}>
              <CronEditor
                current={schedule.cron}
                timezone={schedule.timezone}
                busy={running}
                onCancel={() => setEditingCron(false)}
                onSubmit={(cron) => {
                  onSettings(schedule, { cron });
                  setEditingCron(false);
                }}
              />
            </div>
          )}

          {confirmDelete && (
            <div style={{ marginTop: 4 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12.5 }}>
                <AlertTriangle size={14} style={{ verticalAlign: -2, color: 'var(--warning)' }} />{' '}
                Retirer cette planification de l'annuaire. Elle <strong>réapparaîtra</strong> si
                l'application l'annonce de nouveau au prochain démarrage — c'est un ménage,
                pas une désactivation. Pour empêcher le batch de s'exécuter, mettez-le en pause.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionButton
                  onClick={() => {
                    onDelete(schedule);
                    setConfirmDelete(false);
                  }}
                  disabled={running}
                  Icon={Trash2}
                  label="Confirmer le retrait"
                  tone="danger"
                />
                <ActionButton onClick={() => setConfirmDelete(false)} label="Annuler" tone="ghost" />
              </div>
            </div>
          )}
        </div>
      )}

      {(schedule.runnable || controlled) && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--glass-border)', paddingTop: 12 }}>
          {!confirm ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <ActionButton
                onClick={() => setConfirm('dry')}
                disabled={running}
                Icon={FlaskConical}
                label="Simuler maintenant"
                tone="neutral"
              />
              <ActionButton
                onClick={() => setConfirm('apply')}
                disabled={running}
                Icon={Play}
                label="Exécuter maintenant"
                tone="danger"
              />
            </div>
          ) : (
            <div>
              <p style={{ margin: '0 0 10px', fontSize: 12.5 }}>
                {confirm === 'apply' ? (
                  <>
                    <AlertTriangle
                      size={14}
                      style={{ verticalAlign: -2, color: 'var(--warning)' }}
                    />{' '}
                    Cette exécution <strong>écrira en base</strong>.
                    {/* Deux chaînes distinctes : synocore exécute et répond,
                        une application tierce accuse réception et travaille. */}
                    {isAppControlled(schedule)
                      ? " Elle part immédiatement et dure plusieurs minutes : le résultat apparaîtra dans l'historique."
                      : ' Le contenu déjà importé sera ignoré, et le garde-fou de variation reste actif.'}
                  </>
                ) : (
                  <>Simulation : tout est calculé, <strong>rien n'est écrit</strong>.</>
                )}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionButton
                  onClick={() => {
                    // L'objet complet, pas le type : c'est `app` qui décide si
                    // la commande part vers l'application ou vers le core.
                    onRun(schedule, { apply: confirm === 'apply' });
                    setConfirm(null);
                  }}
                  disabled={running}
                  Icon={running ? Loader2 : confirm === 'apply' ? Play : FlaskConical}
                  label={running ? 'En cours…' : 'Confirmer'}
                  tone={confirm === 'apply' ? 'danger' : 'neutral'}
                />
                <ActionButton
                  onClick={() => setConfirm(null)}
                  label="Annuler"
                  tone="ghost"
                  disabled={running}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ color, Icon, label }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 'var(--radius-pill)',
        border: `1px solid ${color}`,
        color,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={12} /> {label}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', letterSpacing: 0.4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{children}</div>
    </div>
  );
}

function ActionButton({ onClick, disabled, Icon, label, tone }) {
  const styles = {
    danger: { background: 'var(--brand)', color: '#fff', border: '1px solid var(--brand)' },
    neutral: { background: 'transparent', color: 'var(--text)', border: '1px solid var(--glass-border)' },
    ghost: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 14px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...styles[tone],
      }}
    >
      {Icon && <Icon size={14} />} {label}
    </button>
  );
}

export default function SchedulesView({ onRunFinished }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const r = await listSchedules(signal);
      setSchedules(r?.items ?? []);
    } catch (err) {
      if (err?.name !== 'AbortError') setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const handleRun = async (schedule, options) => {
    const type = schedule.type;
    setRunning(type);
    setResult(null);
    try {
      if (isAppControlled(schedule)) {
        // L'application accuse réception (202) sans attendre : une
        // synchronisation dure plusieurs minutes, bien au-delà du timeout d'un
        // reverse proxy. Le résultat se lira dans l'historique.
        const r = await runAppBatch(schedule.app, type, options);
        setResult({ ok: true, accepted: true, app: schedule.app, apply: r?.apply });
      } else {
        const r = await runBatch(type, options);
        setResult({ ok: true, ...r });
      }
      await load();
      // L'historique doit refléter l'exécution qui vient d'avoir lieu.
      onRunFinished?.();
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setRunning(null);
    }
  };

  /**
   * Applique un réglage en s'adressant à celui qui EXÉCUTE le batch : synocore
   * pour les siens, l'application elle-même pour les autres. Donner à l'IdP un
   * chemin d'exécution vers les autres apps en ferait un ordonnanceur.
   */
  const handleSettings = async (schedule, patch) => {
    setRunning(schedule.type);
    setResult(null);
    try {
      const updated =
        schedule.app === 'synocore'
          ? await updateCoreBatchSettings(schedule.type, patch)
          : await updateAppBatchSettings(schedule.app, schedule.type, patch);
      setResult({ ok: true, settings: updated });
      // Recharge : on affiche l'état RELU, jamais celui qu'on a supposé.
      await load();
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setRunning(null);
    }
  };

  const handleDelete = async (schedule) => {
    setRunning(schedule.type);
    setResult(null);
    try {
      await deleteSchedule(schedule.type);
      setResult({ ok: true, deleted: schedule.label });
      await load();
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setRunning(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <CalendarClock size={17} style={{ color: 'var(--brand)' }} />
        <h2 style={{ margin: 0, fontSize: 18 }}>Planification</h2>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text-muted)' }}>
        Batchs planifiés, prochaines exécutions et relance à la demande.
      </p>

      {result && (
        <div
          style={{
            padding: 12,
            marginBottom: 14,
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${result.ok ? 'var(--success)' : 'var(--danger)'}`,
            fontSize: 13,
          }}
        >
          {result.ok && result.settings ? (
            <>
              Réglage enregistré — mode{' '}
              <strong>{result.settings.applies ? 'écriture' : 'simulation'}</strong>, planification{' '}
              <strong>{result.settings.enabled ? 'armée' : 'en pause'}</strong>.
            </>
          ) : result.ok && result.accepted ? (
            <>
              Exécution lancée sur <strong>{result.app}</strong> en mode{' '}
              <strong>{result.apply ? 'écriture' : 'simulation'}</strong> — elle dure plusieurs
              minutes. Le résultat apparaîtra dans l'historique.
            </>
          ) : result.ok ? (
            <>
              Exécution terminée — statut <strong>{result.status}</strong>
              {result.totals && (
                <span style={{ color: 'var(--text-muted)' }}>
                  {' '}
                  · {result.totals.read ?? 0} lues, {result.totals.created ?? 0} créées,{' '}
                  {result.totals.rejected ?? 0} rejetées
                </span>
              )}
            </>
          ) : (
            <span style={{ color: 'var(--danger)' }}>{result.message}</span>
          )}
        </div>
      )}

      {loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          <Loader2 size={14} style={{ verticalAlign: -2 }} /> Chargement…
        </p>
      )}

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: -2 }} />{' '}
          {error instanceof ApiError && error.status === 403
            ? 'Action réservée aux administrateurs.'
            : error.message}
        </p>
      )}

      {!loading &&
        !error &&
        schedules.map((s) => (
          <ScheduleCard
            key={s.type}
            schedule={s}
            onRun={handleRun}
            onSettings={handleSettings}
            onDelete={handleDelete}
            running={running === s.type}
          />
        ))}

      {!loading && !error && schedules.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Aucun batch planifié.</p>
      )}

      <p style={{ marginTop: 18, fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Les batchs de synocore sont relancés par le core ; ceux de synolog et synosight sont
        pilotés directement dans leur application, avec votre session — la console ne fait
        que porter la commande. Un batch dont l'application n'annonce pas de planification
        n'apparaît pas ici : mieux vaut une liste courte qu'une liste incomplète présentée
        comme exhaustive.
      </p>
    </div>
  );
}
